<script setup lang="ts">
import { convert, type AmountUnit } from '@/utils/amount-unit'
import type { FieldState } from '@/utils/field-state'
import { computed, useAttrs } from 'vue'
import { useI18n } from 'vue-i18n'

defineOptions({ inheritAttrs: false })

const props = withDefaults(
  defineProps<{
    referenceValue?: number | null
    placeholder?: string
    ariaLabel?: string
    disabled?: boolean
    state?: FieldState | null
  }>(),
  {
    referenceValue: null,
    disabled: false,
    state: null,
  },
)

const emit = defineEmits<{
  (event: 'unitChange', unit: AmountUnit): void
}>()

const value = defineModel<number | null>({ default: null })
const unit = defineModel<AmountUnit | null>('unit', { default: 'percent' })

const { t, locale } = useI18n({ useScope: 'local' })

const attrs = useAttrs()

const inputAttrs = computed(() => {
  const { class: _class, ...rest } = attrs

  return rest
})

const units: { unit: AmountUnit; symbol: string }[] = [
  { unit: 'euro', symbol: '€' },
  { unit: 'percent', symbol: '%' },
]

const selectedUnit = computed<AmountUnit>(() => unit.value ?? 'percent')

const isPercent = computed(() => selectedUnit.value === 'percent')

const otherUnit = computed<AmountUnit>(() => (isPercent.value ? 'euro' : 'percent'))

const suffix = computed(() => (isPercent.value ? ' %' : ' €'))

function selectUnit(next: AmountUnit): void {
  if (props.disabled || next === selectedUnit.value) {
    return
  }

  value.value = convert(value.value, selectedUnit.value, next, props.referenceValue)
  unit.value = next

  emit('unitChange', next)
}
</script>

<template>
  <div class="w-full min-w-0" :class="attrs.class">
    <div
      class="focus-within:border-action focus-within:ring-action/20 grid w-full min-w-0 grid-cols-[38px_minmax(0,1fr)] items-stretch overflow-hidden rounded-lg border text-right tabular-nums transition-colors focus-within:ring-[3px]"
      :class="
        state === 'missing'
          ? 'border-warning bg-warning-subtle border-dashed'
          : state === 'reading'
            ? 'border-auto-line bg-auto-subtle'
            : [
                state === 'auto'
                  ? 'border-auto-line hover:border-action'
                  : 'border-field-line hover:border-field-line-hover',
                disabled ? 'bg-surface-3' : 'bg-surface',
              ]
      ">
      <button
        type="button"
        :disabled="disabled"
        :aria-label="t('switchUnit', { unit: t(`units.${otherUnit}`) })"
        class="bg-surface-3 border-line focus-visible:ring-action/35 grid shrink-0 content-center justify-items-center border-r px-0 py-px transition-colors focus-visible:ring-[3px] focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-55"
        @click="selectUnit(otherUnit)">
        <span
          v-for="option in units"
          :key="option.unit"
          class="w-5 rounded-[4px] text-center text-xs leading-tight font-bold"
          :class="
            option.unit === selectedUnit
              ? 'bg-surface text-action-hover shadow-[0_1px_3px_rgba(29,38,39,0.14)]'
              : 'text-ink-2'
          ">
          {{ option.symbol }}
        </span>
      </button>

      <FinestInputNumber
        v-bind="inputAttrs"
        v-model="value"
        bare
        :locale="locale"
        :suffix="suffix"
        :placeholder="placeholder"
        :aria-label="ariaLabel"
        :disabled="disabled"
        :state="state"
        class="grid min-w-0 content-center" />
    </div>
  </div>
</template>

<i18n lang="json">
{
  "de": {
    "switchUnit": "Einheit wechseln zu {unit}",
    "units": {
      "euro": "Euro",
      "percent": "Prozent"
    }
  },
  "en": {
    "switchUnit": "Switch unit to {unit}",
    "units": {
      "euro": "euro",
      "percent": "percent"
    }
  }
}
</i18n>
